import React, { useEffect, useRef, useState } from 'react'
import { Box, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material'
import { GlobalStyles } from '@mui/material'
import PrimaryButton from '../components/PrimaryButton'
import axios from '../api/axios';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const UserLogin = () => {

    const [userDetails, setUserDetails] = useState({
        name: "",
        phone: "",
        email: ""
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const [isChecked, setIsChecked] = useState(false);
    const [shake, setShake] = useState(false);
    const idleTimerRef = useRef(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserDetails((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validateForm = () => {
        let formErrors = {};

        if (!userDetails?.name?.trim()) {
            formErrors.name = "Username is required.";
        }

        if (!userDetails?.phone?.trim()) {
            formErrors.phone = "Phone is required.";
        } else if (userDetails?.phone?.length !== 10) {
            formErrors.phone = "Phone number must be exactly 10 digits.";
        }

        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    const handleLogin = async () => {
        if (!validateForm()) return;

        if (!isChecked) {
            setShake(true);
            setTimeout(() => setShake(false), 500);
            return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setIsLoading(false);
                return;
            }

            const userDetailsCopy = { ...userDetails };
            if (userDetailsCopy.email === "") {
                userDetailsCopy.email = null;
            }

            const response = await axios.post('kiosk-user', userDetailsCopy, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response?.data?.status === true) {
                const kioskUser = response?.data?.data?.dbResponse;
                localStorage.setItem("kioskUser", JSON.stringify(kioskUser));
                const successMessage = response?.data?.data?.message;
                toast.success(successMessage, {
                    position: "top-center",
                    autoClose: 2000,
                    theme: "colored",
                    type: "info"
                });
                navigate('/products');
            }

        } catch (error) {
            if (error?.response) {
                let errorMessage = "Something went wrong. Please try again.";
                const status = error.response?.status;

                if (status === 406) {
                    errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                } else if (status === 412) {
                    errorMessage = error?.response?.data?.data?.message;
                } else if (status === 500) {
                    errorMessage = error?.response?.data?.error;
                } else if (status === 401 || status === 403) {
                    errorMessage = "Unauthorized. Please check your credentials.";
                }

                toast.error(errorMessage, {
                    position: "bottom-center",
                    autoClose: 3000,
                    theme: "colored",
                });
            } else {
                toast.error("Network error. Please check your connection.", {
                    position: "bottom-center",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const resetIdleTimer = () => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
        }
        idleTimerRef.current = setTimeout(() => {
            navigate("/explore");
        }, 30000);
    };

    useEffect(() => {
        resetIdleTimer();

        const events = ["keydown", "mousedown", "mousemove", "touchstart"];
        events.forEach((event) => window.addEventListener(event, resetIdleTimer));

        return () => {
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            events.forEach((event) => window.removeEventListener(event, resetIdleTimer));
        };
    }, []);

    return (
        <>
            <GlobalStyles
                styles={`
                    @keyframes slideInLeft {
                        from { transform: translateX(-130%); opacity: 0; }
                        to   { transform: translateX(0);     opacity: 1; }
                    }
                    @keyframes slideInRight {
                        from { transform: translateX(130%); opacity: 0; }
                        to   { transform: translateX(0);    opacity: 1; }
                    }
                `}
            />

            <Box sx={{
                textAlign: "center",
                background: "#71bae5",
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden'
            }}>

                {/* Top-left cloud — slides in from left */}
                <Box sx={{
                    position: 'absolute',
                    top: 100,
                    left: 0,
                    zIndex: 0,
                    animation: 'slideInLeft 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both',
                }}>
                    <img src="/assets/Vector-one.png" alt="cloud-top-left" />
                </Box>

                {/* Top-right cloud — slides in from right */}
                <Box sx={{
                    position: 'absolute',
                    right: 0,
                    top: 200,
                    zIndex: 0,
                    animation: 'slideInRight 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.3s both',
                }}>
                    <img src="/assets/Vector-two.png" alt="cloud-top-right" />
                </Box>

                {/* Form content */}
                <Grid container justifyContent='center' alignItems={'center'} sx={{ flex: 1 }}>
                    <Grid item md={4} sm={6} xs={10}>
                        <img src='/assets/auth-brand-logo.png' alt="brand-logo" style={{ width: '100px' }} />

                        <Typography sx={{ fontSize: "25px", color: 'white', fontWeight: 'bold' }}>
                            Hey!
                            <br />
                            Lets Get You a Cup!
                        </Typography>

                        {/* Name field */}
                        <Box sx={{ textAlign: 'left' }}>
                            <label style={{ color: 'white' }}>What's your name</label>
                            <TextField
                                sx={{
                                    zIndex: 10,
                                    position: "relative",
                                    borderRadius: "8px",
                                    backgroundColor: "#fff"
                                }}
                                autoFocus={false}
                                autoComplete="off"
                                fullWidth
                                name='name'
                                type='text'
                                value={userDetails?.name}
                                placeholder="Tap here to type"
                                variant="outlined"
                                onChange={handleChange}
                                error={!!errors.name}
                            />
                            <p style={{ fontSize: '12px', marginTop: '-1px', color: 'red' }}>{errors.name}</p>
                        </Box>

                        {/* Phone field */}
                        <Box sx={{ textAlign: 'left', marginTop: '21px' }}>
                            <label style={{ color: 'white' }}>
                                What's your Number{" "}
                                <img width={'16'} style={{ marginBottom: '-3px' }} src='/assets/whatsapp.png' alt="whatsapp" />
                            </label>
                            <TextField
                                sx={{
                                    zIndex: 10,
                                    position: "relative",
                                    borderRadius: "8px",
                                    backgroundColor: "#fff"
                                }}
                                autoFocus={false}
                                autoComplete="off"
                                type='text'
                                fullWidth
                                name='phone'
                                placeholder="Tap here to type"
                                variant="outlined"
                                value={userDetails?.phone}
                                onChange={handleChange}
                                error={!!errors.phone}
                            />
                            <p style={{ fontSize: '12px', marginTop: '-1px', color: 'red' }}>{errors.phone}</p>
                        </Box>

                        {/* Email field */}
                        <Box sx={{ textAlign: 'left', marginTop: '21px' }}>
                            <label style={{ color: 'white' }}>What's your Email (optional)</label>
                            <TextField
                                sx={{
                                    zIndex: 10,
                                    position: "relative",
                                    borderRadius: "8px",
                                    backgroundColor: "#fff"
                                }}
                                autoFocus={false}
                                autoComplete="off"
                                type='email'
                                fullWidth
                                name='email'
                                placeholder="Tap here to type"
                                variant="outlined"
                                value={userDetails?.email}
                                onChange={handleChange}
                            />
                        </Box>

                        {/* Terms checkbox */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isChecked}
                                    onChange={(e) => setIsChecked(e.target.checked)}
                                />
                            }
                            label={
                                <span style={{ fontSize: '12px' }}>
                                    I agree to the{" "}
                                    <Link to="/terms-and-conditions" style={{ color: "#1565C0", textDecoration: "none" }}>
                                        Terms and Conditions
                                    </Link>{" "}
                                    &{" "}
                                    <Link to="/privacy-policy" style={{ color: "#1565C0", textDecoration: "none" }}>
                                        Privacy Policy
                                    </Link>
                                </span>
                            }
                            sx={{ color: "white", marginTop: "10px" }}
                        />

                        {/* Submit button with shake animation */}
                        <motion.div animate={shake ? { x: [-10, 10, -10, 10, 0] } : {}}>
                            <PrimaryButton
                                title={'Proceed'}
                                onClick={handleLogin}
                                disabled={isLoading}
                                spinner={isLoading}
                                disabledTitle={'Logging In'}
                                style={{
                                    borderRadius: "30px",
                                    fontSize: "18px",
                                    width: '100%',
                                    marginTop: '10px'
                                }}
                            />
                        </motion.div>
                    </Grid>
                </Grid>

                {/* Bottom-left cloud — slides in from left */}
                <Box sx={{
                    position: 'absolute',
                    bottom: 60,
                    left: 0,
                    zIndex: 0,
                    animation: 'slideInLeft 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both',
                }}>
                    <img src="/assets/Vector-one.png" alt="cloud-bottom-left" />
                </Box>

                {/* Bottom-right cloud — slides in from right */}
                <Box sx={{
                    position: 'absolute',
                    bottom: 20,
                    right: 0,
                    zIndex: 0,
                    animation: 'slideInRight 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.7s both',
                }}>
                    <img src="/assets/Vector-two.png" alt="cloud-bottom-right" />
                </Box>

            </Box>
        </>
    );
};

export default UserLogin;